import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { Address, CreateAddressRequest, UpdateAddressRequest } from '@/shared/types/addressTypes';
import { PaginatedResponse, SingleResponse } from '@/shared/types/responseTypes';

/** Infinite scroll fetch for addresses load-more. */
export const useInfiniteAddresses = (regionCode: string | undefined) => {
  return useInfiniteQuery({
    queryKey: ['region-addresses-infinite', regionCode],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<Address>>(
        `/enterprise/v1/addresses`,
        { params: { regionCode, page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
    enabled: !!regionCode,
  });
};

// Create Address Mutation
export const useCreateAddress = (regionCode: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAddressRequest) => {
      return apiClient.post<SingleResponse<Address>>(
        `/enterprise/v1/addresses`,
        {
          params: { regionCode },
          body: data,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['created-address'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Update Address Mutation
export const useUpdateAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ addressId, data }: { addressId: string, data: UpdateAddressRequest }) => {
      return apiClient.put<APIMessageResponse>(
        `/enterprise/v1/addresses/${addressId}`,
        {
          body: data,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['updated-address'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Delete Address Mutation
export const useDeleteAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ addressId }: { addressId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/addresses/${addressId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deleted-address'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Add Address to Organization Mutation
export const useAddAddressToOrganization = (addressId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: string) => {
      return apiClient.post<APIMessageResponse>(
        `/enterprise/v1/addresses/${addressId}/organizations/${organizationId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['added-address-to-account'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove Address from Organization Mutation
export const useRemoveAddressFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ addressId, organizationId }: { addressId: string; organizationId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/addresses/${addressId}/organizations/${organizationId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['removed-address-from-account'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};